import { useMemo } from "react";
import { Eye } from "lucide-react";

import PageShell from "../../../v2/components/shared/page-shell/page-shell.tsx";
import { Card } from "../../../v2/components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../../v2/components/shared/empty-state-message/empty-state-message.tsx";
import { Badge, Button, Modal, ResponsiveDataTable, Select, Spinner, TextArea, Toggle } from "../../../v2/components/ui/index.ts";
import { useEmployeeNameOptions } from "../../../v2/hooks/use-employee-name-options.ts";
import { useSqeEmployeeNameOptions } from "../../../v2/hooks/use-sqe-employee-name-options.ts";
import { displayFormatDate } from "../../../utils/helpers";
import { formatLeaveTypeLabel } from "../utils/leave-type-label";
import styles from "./leave-approval-view.module.css";

const STATUS = {
  PENDING: { label: "Pending", variant: "warning" },
  APPROVED: { label: "Approved", variant: "success" },
  REJECTED: { label: "Rejected", variant: "danger" },
  WITHDRAW: { label: "Withdrawn", variant: "neutral" },
  WITHDRAWN: { label: "Withdrawn", variant: "neutral" },
};

const ACTION_OPTIONS = [
  { value: "APPROVED", label: "Approve" },
  { value: "REJECTED", label: "Reject" },
];

const DETAIL_COLUMNS = [
  { id: "employeeName", header: "Employee Name", accessor: "employeeName" },
  { id: "fromDate", header: "From", render: (row) => displayFormatDate(row.fromDate) },
  { id: "toDate", header: "To", render: (row) => displayFormatDate(row.toDate) },
  { id: "leaveType", header: "Leave Type", render: (row) => formatLeaveTypeLabel(row.leaveType) },
  { id: "totalDays", header: "Leave Count", accessor: "totalDays" },
  { id: "reason", header: "Reason", render: (row) => row.reason || "-", truncate: true, getTitleText: (row) => String(row.reason ?? "-") },
];

export default function LeaveApprovalView({
  title, description, rows, totalItems, loading, page, pageSize,
  onPageChange, onPageSizeChange, employeeId, onEmployeeChange,
  status, statusOptions, onStatusChange, showManager, showAction,
  canAct, selectedLeave, onSelectLeave, onCloseModal,
  action, actionOptions = ACTION_OPTIONS, onActionChange, reason, onReasonChange, reasonError,
  deductionOptions, deduction, onDeductionChange, deductionError,
  onReasonKeyPress, onSubmit, viewToggle, isSqe = false,
}) {
  const regularEmployeeNames = useEmployeeNameOptions(!isSqe);
  const sqeEmployeeNames = useSqeEmployeeNameOptions(isSqe);
  const employeeNames = isSqe ? sqeEmployeeNames : regularEmployeeNames;
  const columns = useMemo(() => [
    { id: "employeeName", header: "Employee Name", accessor: "employeeName", sortable: true },
    { id: "fromDate", header: "From", sortable: true, getSortValue: (row) => row.fromDate, render: (row) => displayFormatDate(row.fromDate) },
    { id: "toDate", header: "To", sortable: true, getSortValue: (row) => row.toDate, render: (row) => displayFormatDate(row.toDate) },
    ...(showManager ? [{ id: "managerName", header: "Approved By", accessor: "managerName", sortable: true }] : []),
    { id: "leaveType", header: "Leave Type", sortable: true, getSortValue: (row) => formatLeaveTypeLabel(row.leaveType), render: (row) => formatLeaveTypeLabel(row.leaveType) },
    { id: "totalDays", header: "Leave Count", accessor: "totalDays", sortable: true },
    { id: "reason", header: "Reason", accessor: "reason", truncate: true, getTitleText: (row) => String(row.reason ?? "-"), render: (row) => row.reason || "-" },
    { id: "status", header: "Status", nowrap: true, sortable: true, render: (row) => {
      const meta = STATUS[row.status];
      return <Badge variant={meta?.variant ?? "neutral"}>{meta?.label ?? row.status ?? "-"}</Badge>;
    } },
    ...(showAction ? [{ id: "action", header: "Action", nowrap: true, render: (row) => canAct(row) ? (
      <Button size="sm" variant="accent-ghost" startIcon={<Eye size={16} aria-hidden />} onClick={() => onSelectLeave(row)}>View</Button>
    ) : <span className={styles.muted}>-</span> }] : []),
  ], [canAct, onSelectLeave, showAction, showManager]);

  return <PageShell className={styles.page}>
  
    <Card className={styles.filterCard}>
      <div className={styles.filters}>
        <Select id="leave-approval-employee" label="Employee Name" value={employeeId} options={employeeNames.options} placeholder={employeeNames.loading ? "Loading Employees..." : "Select Employee Name"} onChange={onEmployeeChange} disabled={employeeNames.loading} searchable isClearable />
        <Select id="leave-approval-status" label="Status" value={status ?? ""} options={statusOptions} placeholder="Select Status" onChange={onStatusChange} isClearable />
      </div>
      {viewToggle ? <div className={styles.viewToggle}><Toggle checked={viewToggle.active} onCheckedChange={viewToggle.onCheckedChange} label="Exceeded WFH Requests" showStatusText={false} /></div> : null}
    </Card>

    {loading ? <Card><div className={styles.loading}><Spinner /></div></Card> : rows.length ? (
      <ResponsiveDataTable aria-label="Leave requests" columns={columns} data={rows} pagination page={page} pageSize={pageSize} totalItems={totalItems} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} pageSizeOptions={[5, 10, 20, 30, 40]} totalLabel={`Total Leaves: ${totalItems}`} />
    ) : <Card><EmptyStateMessage message="No Record Available" /></Card>}

    <Modal open={selectedLeave != null} onClose={onCloseModal} title="Leave Approval" size="lg" footer={<div className={styles.footer}>
      <Button type="button" variant="light-grey" onClick={onCloseModal}>Cancel</Button>
      <Button type="submit" form="leave-approval-form" disabled={loading}>{loading ? "Submitting..." : "Submit"}</Button>
    </div>}>
      {selectedLeave && <form id="leave-approval-form" onSubmit={onSubmit} className={styles.modalForm}>
        <ResponsiveDataTable aria-label="Selected leave request" columns={DETAIL_COLUMNS} data={[selectedLeave]} pagination={false} />
        <div className={styles.formFields}>
          <Select id="leave-approval-action" label="Action" value={action} options={actionOptions} placeholder="Select Action" onChange={onActionChange} required />
          {deductionOptions ? <Select id="leave-approval-deduction" label="Leave Deduction" value={deduction} options={deductionOptions} placeholder="Select Leave Deduction" onChange={onDeductionChange} error={deductionError} required /> : null}
          <TextArea id="leave-approval-reason" label="Reason" value={reason} onChange={(event) => onReasonChange(event.target.value)} onKeyPress={onReasonKeyPress} error={reasonError} rows={3} required />
        </div>
      </form>}
    </Modal>
  </PageShell>;
}
